import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, Icon, Typography, Card } from "@/components/atoms";
import { useOperator } from "@/hooks";
import { useGetOnboardingStatusQuery, useUpdateSectionStatusMutation } from "@/redux/apis/onboarding/onboardingApi";
import { SimulationSection } from "@/redux/slices/onboarding/types";
import {
  setLastOpenedPanel,
  setHasSeenSimulationCompleteSnackbar,
  setHasSeenPreSimulationModal,
  selectHasSeenSimulationCompleteSnackbar,
  selectHasSeenPreSimulationModal,
} from "@/redux/slices/quickSetup";
import { showSnackbar } from "@/utils";
import { getClientBookingPath } from "../../Clients/helpers";
import { SetupPanel, WalkthroughModal } from "../common";
import { quickSetupContent } from "../data/quickSetupContent";
import { isSectionComplete, isSimulationSectionUnlocked, shouldShowSimulationCompleteSnackbar } from "../helpers/onboardingHelpers";
import { useQuickSetupNavigation } from "../hooks";
import { PreSimulationModal, AddDriverModal } from "../modals";

const SimulationPaths = {
  ADD_CLIENT: "/clients/add",
  CLIENTS: "/clients",
  MY_VEHICLES: "/vehicles/my-vehicles",
  ADD_VEHICLE: "/vehicles/my-vehicles/add",
} as const;

enum ModalType {
  BOOKING_WALKTHROUGH = "booking-walkthrough",
}

export const RunTestSimulationPanel = () => {
  const navigate = useNavigate();
  const { id: operatorId } = useOperator();
  const dispatch = useDispatch();
  const { setQuickSetupFlag } = useQuickSetupNavigation();
  const [activeModal, setActiveModal] = useState<ModalType | null>(null);
  const [showPreSimulationModal, setShowPreSimulationModal] = useState(false);
  const [showAddDriverModal, setShowAddDriverModal] = useState(false);

  const { data: onboardingStatus } = useGetOnboardingStatusQuery();
  const [updateSectionStatus] = useUpdateSectionStatusMutation();
  const hasSeenSimulationCompleteSnackbar = useSelector(selectHasSeenSimulationCompleteSnackbar);
  const hasSeenPreSimulationModal = useSelector(selectHasSeenPreSimulationModal);

  const isCreateClientCompleted = onboardingStatus ? isSectionComplete(onboardingStatus.simulation.createClient) : false;
  const isCreateDriverCompleted = onboardingStatus ? isSectionComplete(onboardingStatus.simulation.createDriver) : false;
  const isCreateVehicleCompleted = onboardingStatus ? isSectionComplete(onboardingStatus.simulation.createVehicle) : false;
  const isCreateBookingCompleted = onboardingStatus ? isSectionComplete(onboardingStatus.simulation.createBooking) : false;

  const isCreateClientValid = onboardingStatus ? onboardingStatus.simulation.createClient.isValid : false;
  const isCreateVehicleValid = onboardingStatus ? onboardingStatus.simulation.createVehicle.isValid : false;

  const completedSteps = [isCreateClientCompleted, isCreateDriverCompleted, isCreateVehicleCompleted, isCreateBookingCompleted].filter(
    Boolean
  ).length;
  const totalSteps = 4;

  const isUnlocked = isSimulationSectionUnlocked(onboardingStatus);
  const hasStartedSimulation = onboardingStatus
    ? isCreateClientCompleted || isCreateDriverCompleted || isCreateVehicleCompleted || isCreateBookingCompleted
    : false;

  const shouldShowSnackbar = shouldShowSimulationCompleteSnackbar(onboardingStatus);

  useEffect(() => {
    if (isUnlocked && !hasStartedSimulation && !hasSeenPreSimulationModal) {
      setShowPreSimulationModal(true);
    } else {
      setShowPreSimulationModal(false);
    }
  }, [isUnlocked, hasStartedSimulation, hasSeenPreSimulationModal]);

  useEffect(() => {
    if (shouldShowSnackbar && !hasSeenSimulationCompleteSnackbar) {
      showSnackbar({
        message: "Test run finished! You can now continue with steps 4 to 6.",
        size: "small",
      });
      dispatch(setLastOpenedPanel("4-fleet-setup"));
      dispatch(setHasSeenSimulationCompleteSnackbar(true));
    }
  }, [shouldShowSnackbar, hasSeenSimulationCompleteSnackbar, dispatch]);

  const handleModalClose = () => setActiveModal(null);

  const handleAddClient = () => {
    setQuickSetupFlag(SimulationSection.CREATE_CLIENT);
    navigate(!isCreateClientValid ? SimulationPaths.ADD_CLIENT : SimulationPaths.CLIENTS);

    if (onboardingStatus && !isCreateClientCompleted) {
      updateSectionStatus({ section: SimulationSection.CREATE_CLIENT, completed: true }).catch((error) => {
        console.error("Failed to update section status:", error);
      });
    }
  };

  const handleAddDriver = () => {
    setShowAddDriverModal(true);

    if (onboardingStatus && !isCreateDriverCompleted) {
      updateSectionStatus({ section: SimulationSection.CREATE_DRIVER, completed: true }).catch((error) => {
        console.error("Failed to update section status:", error);
      });
    }
  };

  const handleAddVehicle = () => {
    setQuickSetupFlag(SimulationSection.CREATE_VEHICLE);
    navigate(isCreateVehicleValid ? SimulationPaths.MY_VEHICLES : SimulationPaths.ADD_VEHICLE);

    if (onboardingStatus && !isCreateVehicleCompleted) {
      updateSectionStatus({ section: SimulationSection.CREATE_VEHICLE, completed: true }).catch((error) => {
        console.error("Failed to update section status:", error);
      });
    }
  };

  const handleWatchBookingVideo = () => {
    setActiveModal(ModalType.BOOKING_WALKTHROUGH);
  };

  const handleClosePreSimulationModal = () => {
    setShowPreSimulationModal(false);
  };

  const handleGoToTesting = () => {
    setShowPreSimulationModal(false);
    dispatch(setHasSeenPreSimulationModal(true));
    dispatch(setLastOpenedPanel("999-custom-panel"));
  };

  const handleCreateBooking = () => {
    const clientMetadata = onboardingStatus?.simulation.createClient.metadata;
    const hasMultipleClients = clientMetadata?.hasMultipleClients ?? false;
    const clientUuid = clientMetadata?.uuid;

    if (!hasMultipleClients && clientUuid) {
      /*
       * The OBO booking sheet for this client, not their record: this step is "Create Booking", and
       * the operator is being walked through making one.
       *
       * A behaviour change, deliberately. The old path opened the client's booking HISTORY in the
       * legacy portal — already the wrong screen for the step, and since the client-portal cutover it
       * redirects to the bookings list instead, so there is no previous behaviour left to preserve.
       */
      // "noopener" is load-bearing — see BookingActionsBar. Without it kinde-auth-react treats the new
      // tab as its own login popup and closes it as the app boots.
      window.open(`${window.location.origin}/dashboard${getClientBookingPath(operatorId, clientUuid)}`, "_blank", "noopener,noreferrer");
    } else {
      setQuickSetupFlag(SimulationSection.CREATE_BOOKING);
      navigate(SimulationPaths.CLIENTS);
    }

    if (onboardingStatus && !isCreateBookingCompleted) {
      updateSectionStatus({ section: SimulationSection.CREATE_BOOKING, completed: true }).catch((error) => {
        console.error("Failed to update section status:", error);
      });
    }
  };

  return (
    <SetupPanel
      stepNumber={999}
      title={
        <div className="flex items-center gap-2">
          <div className="h-2.5 w-2.5 rounded-full bg-danger" />
          <Typography variant="title">Run a Test Scenario in a Live Environment</Typography>
        </div>
      }
      isLocked={!isUnlocked}
      description="This is a real booking environment, so we recommend using actual details you plan to operate with"
      stepCount={totalSteps}
      completedSteps={completedSteps}
      isFeatured
    >
      {/* Pro Tip Section */}
      <div className="rounded-lg border border-info bg-info-light p-4">
        <div className="flex flex-col gap-1">
          <Typography variant="title" className="text-info-dark">
            Pro tip:
          </Typography>
          <ul className="ml-5 list-disc text-info-dark">
            <li>
              <Typography variant="paragraph">
                Use a different email from your admin login when creating client and driver profiles to keep communications organized.
              </Typography>
            </li>
            <li>
              <Typography variant="paragraph">
                You can also book trips weeks or months in advance to avoid being charged during testing — and remember to cancel test
                bookings afterward to prevent actual charges.
              </Typography>
            </li>
          </ul>
        </div>
      </div>

      {/* Test Steps */}
      <div className="flex flex-col gap-4 md:flex-row">
        {/* Create Client Profile */}
        <Card className="flex-1 !bg-neutral-surface-gray">
          <div className="flex h-full flex-col justify-between gap-2.5">
            <div className="flex flex-col gap-1">
              <div className="mb-1 flex h-8 w-8 items-center justify-center">
                <Icon name="User" size="md" className="text-neutral-dark-gray" />
              </div>
              <Typography variant="action">Create 1 Client Profile</Typography>
              <Typography variant="paragraph">Add yourself as a client</Typography>
            </div>
            <Button
              variant="primary"
              size="xs"
              disabled={isCreateClientCompleted}
              className="h-8 w-full"
              endIcon={isCreateClientCompleted ? <Icon name="TickCircle" size="md" className="text-success" variant="Bold" /> : undefined}
              onClick={handleAddClient}
            >
              {isCreateClientCompleted ? "Added" : "Add Client"}
            </Button>
          </div>
        </Card>

        {/* Create Driver Profile */}
        <Card className="flex-1 !bg-neutral-surface-gray">
          <div className="flex h-full flex-col justify-between gap-2.5">
            <div className="flex flex-col gap-1">
              <div className="mb-1 flex h-8 w-8 items-center justify-center">
                <Icon name="UserSquare" size="md" className="text-neutral-dark-gray" />
              </div>
              <Typography variant="action">Create 1 Driver Profile</Typography>
              <Typography variant="paragraph">Add yourself as a driver</Typography>
            </div>
            <Button
              variant="primary"
              size="xs"
              className="h-8 w-full"
              onClick={handleAddDriver}
              disabled={isCreateDriverCompleted}
              endIcon={isCreateDriverCompleted ? <Icon name="TickCircle" size="md" className="text-success" variant="Bold" /> : undefined}
            >
              {isCreateDriverCompleted ? "Added" : "Add Driver"}
            </Button>
          </div>
        </Card>

        {/* Add Vehicle */}
        <Card className="flex-1 !bg-neutral-surface-gray">
          <div className="flex h-full flex-col justify-between gap-2.5">
            <div className="flex flex-col gap-1">
              <div className="mb-1 flex h-8 w-8 items-center justify-center">
                <Icon name="Car" size="md" className="text-neutral-dark-gray" />
              </div>
              <Typography variant="action">Add 1 of your own vehicles</Typography>
              <Typography variant="paragraph">Must be operator-owned</Typography>
            </div>
            <Button
              variant="primary"
              size="xs"
              className="h-8 w-full"
              onClick={handleAddVehicle}
              disabled={isCreateVehicleCompleted}
              endIcon={isCreateVehicleCompleted ? <Icon name="TickCircle" size="md" className="text-success" variant="Bold" /> : undefined}
            >
              {isCreateVehicleCompleted ? "Added" : "Add Vehicle"}
            </Button>
          </div>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="flex flex-col gap-4 md:flex-row">
        {/* Booking Manager */}
        <Card className="flex-1 !bg-neutral-surface-gray !p-4">
          <div className="flex flex-col gap-4">
            <div className="flex h-8 w-8 items-center justify-center">
              <Icon name="Home" size="md" className="text-neutral-dark-gray" />
            </div>
            <div className="flex flex-col gap-1">
              <Typography variant="title">Get to Know the Booking Manager</Typography>
              <Typography variant="paragraph">
                This is the core of the platform—practice managing real bookings to ensure you&apos;re confident using the most important
                tool in your workflow.
              </Typography>
            </div>
            <Button variant="primary" size="xs" className="h-8 w-full" onClick={handleWatchBookingVideo}>
              Watch Video Walkthrough
            </Button>
          </div>
        </Card>

        {/* Create Test Bookings */}
        <Card className="flex-1 !bg-neutral-surface-gray !p-4">
          <div className="flex flex-col gap-4">
            <div className="flex h-8 w-8 items-center justify-center">
              <Icon name="ArchiveBook" size="md" className="text-neutral-dark-gray" />
            </div>
            <div className="flex flex-col gap-1">
              <Typography variant="title">Create Test Bookings</Typography>
              <Typography variant="paragraph">Try creating a few real bookings to see your setup in action</Typography>
            </div>
            <Button
              size="xs"
              className="h-8 w-full"
              disabled={!isCreateClientCompleted || !isCreateDriverCompleted || !isCreateVehicleCompleted || isCreateBookingCompleted}
              endIcon={isCreateBookingCompleted ? <Icon name="TickCircle" size="md" className="text-success" variant="Bold" /> : undefined}
              onClick={handleCreateBooking}
            >
              {isCreateBookingCompleted ? "Created" : "Create Booking"}
            </Button>
          </div>
        </Card>
      </div>

      <WalkthroughModal
        open={activeModal === ModalType.BOOKING_WALKTHROUGH}
        onClose={handleModalClose}
        title="Video Walkthrough (Booking Management)"
        videoUrl={quickSetupContent.general.walkthrough.videoUrl}
      />

      <PreSimulationModal open={showPreSimulationModal} onClose={handleClosePreSimulationModal} onGoToTesting={handleGoToTesting} />
      <AddDriverModal open={showAddDriverModal} onClose={() => setShowAddDriverModal(false)} />
    </SetupPanel>
  );
};
